import React, {Component} from 'react';
import PropTypes from 'prop-types';
import edapWrapper from '@homeaway/edap-wrapper';
import VisibilitySensor from 'react-visibility-sensor';
import {v4} from 'uuid';
import {SVGIcon} from '@homeaway/react-svg';
import {CLOSE__16} from '@homeaway/svg-defs';
import Confetti from '../Confetti';
import EmailForm from '../EmailForm';
import EmailSuccess from '../EmailSuccess';
import EmailFail from '../EmailFail';
import './EmailCapture.less';

const DEFAULT_LABELS = {
    emailFail: null,
    emailForm: null,
    emailSuccess: null
};

const CSRF_HEADER = 'X-CSRF-TOKEN';

class EmailCapture extends Component {
    static STEPS = {
        INITIAL: 1,
        SUCCESS: 2,
        FAIL: 3,
        CLOSED: 4
    };

    static propTypes = {
        /** EDAP actionlocation for all events */
        actionLocation: PropTypes.string,
        /** Human readable site brand name */
        brandName: PropTypes.string,
        /** Whether to display/show the component or not */
        display: PropTypes.bool.isRequired,
        /** Display mode */
        displayMode: PropTypes.oneOf(['inline', 'popup']).isRequired,
        /** URL for "Report a problem" link */
        helpUrl: PropTypes.string,
        /** Maximum number of retries before displaying the "Report a problem" link */
        maxRetriesToEnableReporting: PropTypes.number,
        /** Labels to display */
        labels: PropTypes.shape({
            emailForm: PropTypes.shape({
                buttonText: PropTypes.string,
                errorMessage: PropTypes.string,
                heading: PropTypes.string,
                inputPlaceholder: PropTypes.string,
                legalNotice: PropTypes.string,
                privacyPolicy: PropTypes.string,
                subheading: PropTypes.string,
                termsAndConditions: PropTypes.string
            }),
            emailSuccess: PropTypes.shape({
                heading: PropTypes.string,
                subheading: PropTypes.string
            }),
            emailFail: PropTypes.shape({
                buttonText: PropTypes.string,
                heading: PropTypes.string,
                reportLink: PropTypes.string,
                subheading: PropTypes.string
            }),
        }).isRequired,
        /** Callback that will be called when widget is closed by user */
        onClose: PropTypes.func,
        /** Name of the current page displayed (same value Analytics Data Layer consumes) */
        pageName: PropTypes.string.isRequired,
        /** Type of the current page displayed (same value Analytics Data Layer consumes) */
        pageType: PropTypes.string.isRequired,
        /** URL to privacy policy page */
        privacyPolicyUrl: PropTypes.string.isRequired,
        /** Path where server route has been registered for this component */
        routePath: PropTypes.string.isRequired,
        /** URL to terms and conditions page */
        termsAndConditionsUrl: PropTypes.string.isRequired
    };

    static defaultProps = {
        actionLocation: 'react-email-capture',
        brandName: 'HomeAway',
        display: true,
        displayMode: 'inline',
        helpUrl: 'https://help.homeaway.com/articles/How-do-I-submit-feedback',
        labels: DEFAULT_LABELS,
        onClose: () => {},
        pageName: '',
        pageType: '',
        privacyPolicyUrl: 'https://www.homeaway.com/info/about-us/legal/privacy-policy',
        routePath: '/api/email-capture',
        termsAndConditionsUrl: 'https://www.homeaway.com/info/about-us/legal/terms-conditions',
        maxRetriesToEnableReporting: 2
    };

    constructor(props) {
        super(props);
        const {INITIAL, CLOSED} = this.steps;
        this.containerElement = React.createRef();
        this.state = {
            email: '',
            containerHeight: 0,
            retriesCount: 0,
            step: props.display ? INITIAL : CLOSED,
            isActive: true
        };
    }

    get edap() {
        return edapWrapper();
    }

    get steps() {
        return this.constructor.STEPS;
    }

    formatRoutePath(routePath) {
        return `/${routePath.replace(/^\/|\/$/g, '')}`;
    }

    handleValidForm = (email) => {
        const {FAIL, SUCCESS} = this.steps;
        const {pageName, pageType, routePath, actionLocation} = this.props;
        const path = this.formatRoutePath(routePath);
        const submitId = v4();

        this.setState({
            containerHeight: this.containerElement.current.clientHeight
        });

        return fetch(path, {
            method: 'POST',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
                [CSRF_HEADER]: window.ha.csrfToken
            },
            body: JSON.stringify({
                email,
                submitId,
                pageName,
                pageType
            })
        }).then((res) => {
            if (res.ok === false) {
                throw new Error('@homeaway/react-email-capture: client post error');
            }

            const newCsrfToken = res.headers.get(CSRF_HEADER);
            if (newCsrfToken) {
                window.ha.csrfToken = newCsrfToken;
            }

            this.edap.push((edap) => edap.trigger('email.form.submit', {
                actionlocation: actionLocation,
                eventrefinements: `mode:${this.props.displayMode}`,
                formuuid: submitId
            }));

            this.setState({email, step: SUCCESS});
        }).catch(() => {
            this.setState({email, step: FAIL});
        });
    }

    handleTryAgain = () => {
        this.setState((state) => ({
            step: this.steps.INITIAL,
            retriesCount: state.retriesCount + 1
        }));
    }

    getEdapEventCategory = () => {
        const {INITIAL, SUCCESS} = this.steps;
        const {step} = this.state;

        switch (step) {
            case INITIAL:
                return 'email-capture-form';
            case SUCCESS:
                return 'email-success-form';
            default:
                return 'email-error-form';
        }
    }

    handleClose = () => {
        const eventCategory = this.getEdapEventCategory();

        this.setState({step: this.steps.CLOSED});
        this.props.onClose();

        this.edap.push((edap) => edap.trigger('generic.event', {
            actionlocation: this.props.actionLocation,
            eventaction: 'form-dismissed',
            eventcategory: eventCategory,
            eventrefinements: `mode:${this.props.displayMode}`
        }));
    }

    handleVisibilityChange = (isVisible) => {
        if (isVisible && this.state.isActive) {
            this.edap.push((edap) => edap.trigger('page.module.view', {
                actionlocation: this.props.actionLocation,
                eventrefinements: `mode:${this.props.displayMode}`
            }));

            this.setState({
                isActive: false
            });
        }
    }

    renderBackground() {
        const {SUCCESS} = this.steps;
        const {step} = this.state;

        return step === SUCCESS && <Confetti />;
    }

    renderCloseButton() {
        const {displayMode} = this.props;

        return displayMode === 'popup' && (
            <button type="button" onClick={this.handleClose} className="close EmailCapture__close-btn" aria-label={'Close'}>
                <span aria-hidden="true">
                    <SVGIcon markup={CLOSE__16} />
                </span>
            </button>
        );
    }

    renderBody() {
        const {INITIAL, SUCCESS} = this.steps;
        const {
            labels: {
                emailFail,
                emailForm,
                emailSuccess
            },
            brandName,
            helpUrl,
            privacyPolicyUrl,
            termsAndConditionsUrl,
            maxRetriesToEnableReporting
        } = this.props;
        const {
            email,
            step,
            retriesCount,
            containerHeight
        } = this.state;

        switch (step) {
            case INITIAL:
                return <EmailForm {...{brandName, email, privacyPolicyUrl, termsAndConditionsUrl}} labels={emailForm} onValidForm={this.handleValidForm}/>;
            case SUCCESS:
                return <EmailSuccess labels={emailSuccess} height={containerHeight}/>;
            default:
                return <EmailFail {...{helpUrl, maxRetriesToEnableReporting, retriesCount}} labels={emailFail} height={containerHeight} handleTryAgain={this.handleTryAgain}/>;
        }
    }

    render() {
        const {CLOSED} = this.steps;
        const {displayMode, labels} = this.props;
        const {step} = this.state;

        const nullLabels = Object.keys(DEFAULT_LABELS)
            .some((formLabels) => !labels[formLabels]);

        if (step === CLOSED || nullLabels) {
            return null;
        }

        return (
            <React.Fragment>
                <div ref={this.containerElement} className={`EmailCapture EmailCapture--${displayMode} bg-ha-gray-dark`}>
                    {this.renderBackground()}
                    {this.renderCloseButton()}
                    <div className="EmailCapture__body">{this.renderBody()}</div>
                </div>
                <VisibilitySensor onChange={this.handleVisibilityChange} active={this.state.isActive}>
                    <div style={{minHeight: '1px'}} />
                </VisibilitySensor>
            </React.Fragment>
        );
    }
}

export default EmailCapture;
